import React, { useEffect, useState } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import './productModal.css';
import { getProductsBySupplierCode } from 'services/stock-location/productService';
import PropTypes from 'prop-types';

const ProductModal = ({ show, onHide, supplierCode }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const columns = [
    { header: 'PART NO', accessor: 'partNo' },
    { header: 'DESCRIPTION', accessor: 'description' },
    { header: 'VPN', accessor: 'vpn' },
    { header: 'VENDOR DESC', accessor: 'vendorDesc' },
    { header: 'PRIMARY VENDOR', accessor: 'primaryVendor' },
    { header: 'COST', accessor: 'cost' },
    { header: 'LEAD TIME', accessor: 'leadTime' },
    { header: 'MOQ', accessor: 'moq' },
    { header: 'ORDER MULTIPLE', accessor: 'orderMultiple' },
    { header: 'PLANNER NO', accessor: 'plannerNo' }
  ];

  const mapData = items => {
    return items.map(x => ({
      partNo: x.productCode,
      description: x.productDescription,
      vpn: x.supplierCode,
      vendorDesc: x.vendorDesc,
      primaryVendor: x.supplierCode,
      cost: Number(x.cost).toFixed(2),
      leadTime: x.leadTime,
      moq: x.moq,
      orderMultiple: x.orderMultiple,
      plannerNo: x.plannerNo
    }));
  };
  const fetchData = async () => {
    if (!supplierCode) return;

    try {
      setLoading(true);
      const res = await getProductsBySupplierCode(supplierCode);

      const raw = res?.data || res || [];

      setData(mapData(raw));
    } catch (err) {
      console.error('Fetch products error:', err);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show) {
      fetchData();
    }
  }, [show, supplierCode]);

  const exportExcel = () => {
    const formatted = data.map(row => {
      let obj = {};
      columns.forEach(col => {
        obj[col.header] = row[col.accessor];
      });
      return obj;
    });

    const ws = XLSX.utils.json_to_sheet(formatted);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Products');

    const excelBuffer = XLSX.write(wb, {
      bookType: 'xlsx',
      type: 'array'
    });

    const file = new Blob([excelBuffer], {
      type: 'application/octet-stream'
    });

    saveAs(file, 'products.xlsx');
  };

  const handlePrint = () => {
    const content = document.getElementById('print-area');

    const printWindow = window.open('', '', 'width=900,height=700');

    printWindow.document.write(`
    <html>
      <head>
        <title>Print</title>
        <style>
          body { font-family: Arial; padding: 20px; }
          table { width: 100%; border-collapse: collapse; }
          th { background: #3d6b2f; color: white; padding: 8px; border: 1px solid #000; }
          td { padding: 8px; border: 1px solid #000; text-align: center; }
          h3 { margin-bottom: 10px; }
        </style>
      </head>
      <body>
        <h3>Supplier Products / Vendor Master</h3>
        ${content.innerHTML}
      </body>
    </html>
  `);

    printWindow.document.close();
    printWindow.focus();

    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton className="modal-header-custom">
        <Modal.Title>Supplier Products / Vendor Master</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Buttons */}
        <div className="toolbar">
          <Button
            className="btn-green"
            onClick={exportExcel}
            disabled={loading}
          >
            Export Excel
          </Button>

          <Button className="btn-dark" onClick={handlePrint}>
            Print / PDF
          </Button>
        </div>

        {/* Table */}
        <div className="table-wrapper" id="print-area">
          {loading ? (
            <div style={{ textAlign: 'center', padding: 20 }}>
              <Spinner animation="border" />
            </div>
          ) : (
            <table className="custom-table">
              <thead>
                <tr>
                  {columns.map((col, i) => (
                    <th key={i}>{col.header}</th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {data.length > 0 ? (
                  data.map((row, i) => (
                    <tr key={i}>
                      {columns.map((col, j) => (
                        <td key={j}>{row[col.accessor]}</td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={columns.length}
                      style={{ textAlign: 'center' }}
                    >
                      No data
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

ProductModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func,
  supplierCode: PropTypes.string
};

export default ProductModal;
